import { useEffect, useId, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Question, SelectOption, SelectOptionsSource } from '../../config/types';
import { objectToString, stringToObject, validateField } from '../../utils/form-utils';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

const parseProgrammeValue = (value: string) => {
  const parsed = stringToObject(value);
  return {
    programme: typeof parsed?.programme === 'string' ? parsed.programme : '',
    cohortYear: typeof parsed?.cohortYear === 'string' ? parsed.cohortYear : '',
    otherProgramme: typeof parsed?.otherProgramme === 'string' ? parsed.otherProgramme : '',
  };
};

const getProgrammeOptions = (options?: SelectOptionsSource) => {
  if (!Array.isArray(options)) return [];
  return options
    .map((option) => {
      if (typeof option === 'string') return option;
      if ('option' in option) return option.option;
      return '';
    })
    .filter((option) => option.trim() !== '');
};

const getCohortYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 12 }, (_, idx) => String(currentYear - idx));
};

export function DevelopmentProgramme({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);
  const id = useId();
  const baseId = anchorId ?? id;

  const initialValue = useMemo(
    () => String(getValues(question.name) ?? ''),
    [getValues, question.name],
  );
  const initialParsed = useMemo(() => parseProgrammeValue(initialValue), [initialValue]);

  const [programme, setProgramme] = useState(initialParsed.programme);
  const [cohortYear, setCohortYear] = useState(initialParsed.cohortYear);
  const [otherProgramme, setOtherProgramme] = useState(initialParsed.otherProgramme);

  const programmeOptions = useMemo(() => getProgrammeOptions(question.options), [question.options]);
  const cohortYearOptions = useMemo(() => getCohortYearOptions(), []);

  const updateValue = (nextProgramme: string, nextCohortYear: string, nextOtherProgramme: string) => {
    const hasAnyValue = nextProgramme || nextCohortYear || nextOtherProgramme;
    const combined = hasAnyValue
      ? objectToString({
          programme: nextProgramme,
          cohortYear: nextCohortYear,
          otherProgramme: nextOtherProgramme,
        })
      : '';
    setValue(question.name, combined, {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  useEffect(() => {
    if (getValues(question.name) === undefined) {
      setValue(question.name, '');
    }
  }, [getValues, question.name, setValue]);

  const { ref } = register(question.name, {
    validate: (value: string) => {
      const parsed = parseProgrammeValue(value);

      if (!question.rules?.required && !parsed.programme && !parsed.cohortYear && !parsed.otherProgramme) {
        return true;
      }

      if (!parsed.programme) {
        return validateField(question.title, { required: true }, '', question.errorMessages);
      }
      if (!parsed.cohortYear) {
        return 'Please select a cohort year';
      }
      if (parsed.programme === 'Other' && !parsed.otherProgramme.trim()) {
        return 'Please enter the other development programme';
      }
      return true;
    },
  });

  return (
    <>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <input type="hidden" ref={ref} name={question.name} />

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <label className="govuk-label" htmlFor={`${baseId}-programme`}>
            Development programme
          </label>
          <select
            id={`${baseId}-programme`}
            className="govuk-select"
            disabled={disabled}
            value={programme}
            onChange={(event) => {
              const nextProgramme = event.target.value;
              const nextOtherProgramme = nextProgramme === 'Other' ? otherProgramme : '';
              setProgramme(nextProgramme);
              if (nextProgramme !== 'Other') {
                setOtherProgramme('');
              }
              updateValue(nextProgramme, cohortYear, nextOtherProgramme);
            }}
          >
            <option value="">Select programme</option>
            {programmeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="govuk-label" htmlFor={`${baseId}-cohort-year`}>
            Cohort year
          </label>
          <select
            id={`${baseId}-cohort-year`}
            className="govuk-select"
            disabled={disabled}
            value={cohortYear}
            onChange={(event) => {
              const nextCohortYear = event.target.value;
              setCohortYear(nextCohortYear);
              updateValue(programme, nextCohortYear, otherProgramme);
            }}
          >
            <option value="">Select year</option>
            {cohortYearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {programme === 'Other' && (
        <div className="govuk-!-margin-top-3">
          <label className="govuk-label" htmlFor={`${baseId}-other-programme`}>
            Other development programme
          </label>
          <input
            id={`${baseId}-other-programme`}
            type="text"
            className="govuk-input govuk-!-width-two-thirds"
            placeholder="Please specify..."
            disabled={disabled}
            value={otherProgramme}
            onChange={(event) => {
              const nextOtherProgramme = event.target.value;
              setOtherProgramme(nextOtherProgramme);
              updateValue(programme, cohortYear, nextOtherProgramme);
            }}
          />
        </div>
      )}
    </>
  );
}

